/**
 * Tender Overview store (Specs/tender-summary-spec.md).
 *
 * Drives the Tender Summary tab: `load` hydrates the persisted overview via
 * REST; `generate` runs the extraction-stream Function URL for the
 * `tender_overview` category. That category emits progress phases only (no
 * `extraction:item` events), so on completion the fresh overview is re-fetched
 * via REST rather than assembled from stream items.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import { getTenderOverview } from '@/lib/api'
import { streamExtraction, isExtractionStreamingAvailable } from '@/lib/extraction-client'
import type { ExtractionPhase } from '@/types/extraction'
import type { TenderOverview } from '@/types/tender-overview'

// ===================
// Types
// ===================

export type TenderOverviewStatus = 'idle' | 'loading' | 'generating' | 'error'

interface TenderOverviewState {
  /** Bid the current overview belongs to (stores are global). */
  bidId: string | null
  overview: TenderOverview | null
  status: TenderOverviewStatus
  /** Latest pipeline phase reported during generation. */
  phase: ExtractionPhase | null
  error: string | null

  load: (bidId: string) => Promise<void>
  generate: (bidId: string) => Promise<void>
  reset: () => void
}

// ===================
// Module state
// ===================

/** Cancels an in-flight generation stream on re-entry or reset. */
let generateController: AbortController | null = null

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

// ===================
// Store
// ===================

export const useTenderOverviewStore = create<TenderOverviewState>()(
  devtools(
    (set, get) => ({
      bidId: null,
      overview: null,
      status: 'idle',
      phase: null,
      error: null,

      load: async (bidId) => {
        // A live generation for this same bid owns the state — never downgrade it
        // to 'loading' (that would defeat the landing guard and re-show the CTA
        // mid-run). Generation's own completion refetch supplies the data.
        if (get().bidId === bidId && get().status === 'generating') return

        // Bid-identity gate: when switching bids, drop the prior overview up front
        // so bid A's summary never renders under bid B while B's load is in flight.
        const isNewBid = get().bidId !== bidId
        // Switching bids abandons any in-flight generation for the old bid: abort
        // it so its completion refetch can't land the old bid's overview under the
        // new one (its landing writes are also bid-gated via isSuperseded).
        if (isNewBid) {
          generateController?.abort()
          generateController = null
        }
        set({
          bidId,
          status: 'loading',
          error: null,
          ...(isNewBid ? { overview: null } : {}),
        })
        try {
          const { overview } = await getTenderOverview(bidId)
          if (get().bidId !== bidId) return
          // A generate started mid-load (clobber race) — a load resolution must
          // never rewrite state while a generation is running.
          if (get().status === 'generating') return
          set({ overview, status: 'idle' })
        } catch (error) {
          if (get().bidId !== bidId) return
          if (get().status === 'generating') return
          set({ status: 'error', error: errorMessage(error, 'Failed to load tender summary') })
        }
      },

      generate: async (bidId) => {
        if (!isExtractionStreamingAvailable()) {
          set({
            bidId,
            status: 'error',
            error: 'Tender summary generation is not available. Please try again later.',
          })
          return
        }

        // Cancel any prior in-flight run before starting a new one.
        generateController?.abort()
        const controller = new AbortController()
        generateController = controller

        set({ bidId, status: 'generating', phase: null, error: null })

        // A run is "live" only while it owns generateController AND the store is
        // still pointed at its bid. A superseded run (replaced by another generate,
        // or orphaned when the user navigated to a different bid) must never write
        // state — its abort-driven rejection or late refetch would otherwise clobber
        // the run/bid that replaced it (e.g. landing bid A's overview under bid B).
        const isSuperseded = () => generateController !== controller || get().bidId !== bidId

        let streamErrored = false
        try {
          await streamExtraction(
            { bidId, category: 'tender_overview' },
            {
              onProgress: (phase) => {
                if (!isSuperseded()) set({ phase })
              },
              onItem: () => {},
              onComplete: () => {},
              onError: (message) => {
                if (isSuperseded()) return
                streamErrored = true
                set({ status: 'error', error: message, phase: null })
              },
            },
            controller.signal
          )
        } catch (error) {
          if (isSuperseded()) return
          streamErrored = true
          // An `extraction:error` event already set a precise message — don't clobber it.
          if (get().status !== 'error') {
            set({
              status: 'error',
              error: errorMessage(error, 'Tender summary generation failed'),
              phase: null,
            })
          }
          return
        }

        if (streamErrored || isSuperseded()) return

        // No item events for this category — re-fetch the persisted result.
        try {
          const { overview } = await getTenderOverview(bidId)
          if (isSuperseded()) return
          set({ overview, status: 'idle', phase: null })
        } catch (error) {
          if (isSuperseded()) return
          set({ status: 'error', error: errorMessage(error, 'Failed to load tender summary'), phase: null })
        }
      },

      reset: () => {
        generateController?.abort()
        generateController = null
        set({ bidId: null, overview: null, status: 'idle', phase: null, error: null })
      },
    }),
    { name: 'tender-overview-store' }
  )
)
